import { api } from "../auth/authApi";
import type {
  Paginated,
  UUID,
  Client,
  ClientCreate,
  ClientListParams,
  Provider,
  ProviderCreate,
  ProviderListParams,
  Location,
  LocationCreate,
  LocationListParams,
  BulkCreateLocationsBody,
  BulkCreateResult,
} from "./types";

// Helper: remove empty query params
const cleanParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
};

export const masterdataApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // =========================
    // CLIENTS
    // =========================
    listClients: builder.query<Paginated<Client>, ClientListParams | void>({
      query: (params) => ({
        url: "/clients/",
        method: "GET",
        params: cleanParams(params || {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((c) => ({ type: "Clients" as const, id: c.id })),
              { type: "Clients" as const, id: "LIST" },
            ]
          : [{ type: "Clients" as const, id: "LIST" }],
    }),

    getClient: builder.query<Client, UUID>({
      query: (id) => ({ url: `/clients/${id}/`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "Clients", id }],
    }),

    createClient: builder.mutation<Client, ClientCreate>({
      query: (body) => ({ url: "/clients/", method: "POST", body }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),

    updateClient: builder.mutation<Client, { id: UUID; patch: Partial<ClientCreate> }>({
      query: ({ id, patch }) => ({ url: `/clients/${id}/`, method: "PATCH", body: patch }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Clients", id: arg.id }, { type: "Clients", id: "LIST" }],
    }),

    deleteClient: builder.mutation<{ success?: true } | void, { id: UUID }>({
      query: ({ id }) => ({ url: `/clients/${id}/`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Clients", id: arg.id }, { type: "Clients", id: "LIST" }],
    }),

    archiveClient: builder.mutation<{ is_archived: boolean }, { id: UUID }>({
      query: ({ id }) => ({ url: `/clients/${id}/archive/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Clients", id: arg.id }, { type: "Clients", id: "LIST" }],
    }),

    // =========================
    // PROVIDERS
    // =========================
    listProviders: builder.query<Paginated<Provider>, ProviderListParams | void>({
      query: (params) => ({
        url: "/providers/",
        method: "GET",
        params: cleanParams(params || {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((p) => ({ type: "Providers" as const, id: p.id })),
              { type: "Providers" as const, id: "LIST" },
            ]
          : [{ type: "Providers" as const, id: "LIST" }],
    }),

    getProvider: builder.query<Provider, UUID>({
      query: (id) => ({ url: `/providers/${id}/`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "Providers", id }],
    }),

    createProvider: builder.mutation<Provider, ProviderCreate>({
      query: (body) => ({ url: "/providers/", method: "POST", body }),
      invalidatesTags: [{ type: "Providers", id: "LIST" }],
    }),

    updateProvider: builder.mutation<Provider, { id: UUID; patch: Partial<ProviderCreate> }>({
      query: ({ id, patch }) => ({ url: `/providers/${id}/`, method: "PATCH", body: patch }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Providers", id: arg.id }, { type: "Providers", id: "LIST" }],
    }),

    deleteProvider: builder.mutation<{ success?: true } | void, { id: UUID }>({
      query: ({ id }) => ({ url: `/providers/${id}/`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Providers", id: arg.id }, { type: "Providers", id: "LIST" }],
    }),

    archiveProvider: builder.mutation<{ is_archived: boolean }, { id: UUID }>({
      query: ({ id }) => ({ url: `/providers/${id}/archive/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Providers", id: arg.id }, { type: "Providers", id: "LIST" }],
    }),

    // =========================
    // LOCATIONS
    // =========================
    listLocations: builder.query<
      Paginated<Location>,
      (LocationListParams & { stopKey?: string }) | void
    >({
      query: (arg) => {
        const { stopKey: _skip, ...params } = (arg || {}) as LocationListParams & { stopKey?: string };
        return {
          url: "/locations/",
          method: "GET",
          params: cleanParams(params),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((l) => ({ type: "Locations" as const, id: l.id })),
              { type: "Locations" as const, id: "LIST" },
            ]
          : [{ type: "Locations" as const, id: "LIST" }],
    }),

    getLocation: builder.query<Location, UUID>({
      query: (id) => ({ url: `/locations/${id}/`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "Locations", id }],
    }),

    createLocation: builder.mutation<Location, LocationCreate>({
      query: (body) => ({ url: "/locations/", method: "POST", body }),
      invalidatesTags: [{ type: "Locations", id: "LIST" }],
    }),

    updateLocation: builder.mutation<Location, { id: UUID; patch: Partial<LocationCreate> }>({
      query: ({ id, patch }) => ({ url: `/locations/${id}/`, method: "PATCH", body: patch }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Locations", id: arg.id }, { type: "Locations", id: "LIST" }],
    }),

    deleteLocation: builder.mutation<{ success?: true } | void, { id: UUID }>({
      query: ({ id }) => ({ url: `/locations/${id}/`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Locations", id: arg.id }, { type: "Locations", id: "LIST" }],
    }),

    archiveLocation: builder.mutation<{ is_archived: boolean }, { id: UUID }>({
      query: ({ id }) => ({ url: `/locations/${id}/archive/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Locations", id: arg.id }, { type: "Locations", id: "LIST" }],
    }),

    bulkCreateLocations: builder.mutation<BulkCreateResult, BulkCreateLocationsBody>({
      query: (body) => ({ url: "/locations/bulk/", method: "POST", body }),
      invalidatesTags: [{ type: "Locations", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Clients
  useListClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useArchiveClientMutation,

  // Providers
  useListProvidersQuery,
  useGetProviderQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
  useDeleteProviderMutation,
  useArchiveProviderMutation,

  // Locations
  useListLocationsQuery,
  useGetLocationQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useArchiveLocationMutation,
  useBulkCreateLocationsMutation,
} = masterdataApi;
